import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { productsApi } from "../../api/products";
import type { ColorVariant, DraftValidation } from "../../api/types";
import { ErrorNotice } from "../../components/ui";
import {
  PHASE_LABELS,
  type ImportState,
} from "../../import/useGlassesImport";
import { ROLE_LABELS } from "../../import/glassesFolderParser";
import { useProductDetail } from "../useProduct";

/**
 * The import while it is happening, and the product once it has.
 *
 * Every state shown here is one the backend actually reported. There is no
 * percentage bar: the worker reports a job state, not a fraction of a product,
 * and a bar that advances on a timer is a picture of nothing. What an operator
 * needs to know is which stage the work is at and whether anything is wrong,
 * and both of those are true statements.
 */

const PHASE_BLURB: Record<string, string> = {
  preparing: "Getting ready to send the photographs.",
  uploading: "Sending the photographs to storage.",
  queued: "The photographs are stored, waiting for image preparation to begin.",
  running: "Building the pictures customers will see. This takes a moment.",
  creating: "Making the product and filling in what you entered.",
  review_needed: "The pictures were built, but something needs a look.",
  complete: "The product is ready to check.",
  unavailable: "Image preparation is temporarily unavailable.",
  cancelled: "The import was cancelled.",
  failed: "The import stopped.",
};

/** The five pictures the pipeline produces for every colour. */
const DERIVATIVE_ROLES = [
  ["listing_card", "Listing card"],
  ["gallery_front", "Gallery — front"],
  ["gallery_side", "Gallery — side"],
  ["gallery_angle", "Gallery — angle"],
  ["thumbnail", "Thumbnail"],
] as const;

/** Elapsed wait from real seconds. Never a percentage: the worker reports a
 *  state, not a fraction, and a bar advancing on a timer is a picture of
 *  nothing. */
function formatWaiting(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}m ${String(rest).padStart(2, "0")}s`;
}

function PhaseLine({ state }: { state: ImportState }) {
  const done = state.files.filter((f) => f.status === "sent").length;
  return (
    <div className="phase" data-testid="import-phase">
      <span className={`state state--${state.phase}`}>{PHASE_LABELS[state.phase]}</span>
      <span className="phase__blurb">{PHASE_BLURB[state.phase] ?? ""}</span>
      {state.phase === "uploading" ? (
        <span className="phase__count">
          {done} of {state.files.length} photographs sent
        </span>
      ) : null}
      {state.phase === "queued" && state.waitingSeconds !== null ? (
        <span className="phase__count" data-testid="waiting-elapsed">
          Waiting {formatWaiting(state.waitingSeconds)}
        </span>
      ) : null}
    </div>
  );
}

/**
 * One colour's pictures: what was handed over, and what came back.
 *
 * Shown side by side because the question being answered is whether the
 * generated picture is a faithful, well-composed version of the photograph it
 * came from. They are drawn with contain, never cover — a cropped preview would
 * hide exactly the framing faults this screen exists to catch.
 */
function VariantReview({ variant }: { variant: ColorVariant }) {
  const byRole = new Map(variant.images.map((image) => [image.image_role, image]));
  return (
    <article className="variantReview">
      <header className="variantReview__head">
        <span
          className="swatch__dot"
          style={{ backgroundColor: variant.swatch_hex }}
          aria-hidden="true"
        />
        <h4 className="variantReview__name">{variant.name}</h4>
        <span className="faint">
          {variant.stock_quantity === 0
            ? "Out of stock"
            : `${variant.stock_quantity} in stock`}
          {variant.size_options.length > 0
            ? ` · ${variant.size_options.join(", ")}`
            : ""}
        </span>
      </header>

      <div className="derivatives">
        {DERIVATIVE_ROLES.map(([role, label]) => {
          const image = byRole.get(role);
          return (
            <figure className="derivative" key={role}>
              {image ? (
                <img
                  className="derivative__img"
                  src={image.public_url}
                  alt={`${label} for ${variant.name}`}
                  loading="lazy"
                />
              ) : (
                <div className="derivative__missing">Not produced</div>
              )}
              <figcaption className="derivative__cap">
                <span>{label}</span>
                {image ? (
                  <span className="faint">
                    {image.width}×{image.height}
                  </span>
                ) : null}
              </figcaption>
            </figure>
          );
        })}
      </div>
    </article>
  );
}

function ValidationIssues({ validation }: { validation: DraftValidation }) {
  if (validation.publishable && validation.warnings.length === 0) {
    return (
      <div className="notice notice--success">
        Everything needed is in place. This product is ready to publish.
      </div>
    );
  }
  return (
    <div className="stack stack--tight">
      {validation.blocking.length > 0 ? (
        <div className="notice notice--error">
          <strong>These have to be fixed before publishing</strong>
          <ul>
            {validation.blocking.map((issue) => (
              <li key={`${issue.code}-${issue.field}-${issue.message}`}>
                {issue.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {validation.warnings.length > 0 ? (
        <div className="notice notice--warning">
          <strong>Worth checking</strong>
          <ul>
            {validation.warnings.map((issue) => (
              <li key={`${issue.code}-${issue.field}-${issue.message}`}>
                {issue.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function ImportRunPanel({
  state,
  onRetryFile,
}: {
  state: ImportState;
  /** Sends one photograph again. Only the failed one; nothing else moves. */
  onRetryFile: (relativePath: string) => void;
}) {
  const navigate = useNavigate();
  const productId = state.productId;
  const detail = useProductDetail(productId ?? "");

  const validate = useMutation({
    mutationFn: () => productsApi.validateDraft(productId!),
  });

  const publish = useMutation({
    mutationFn: () => {
      const data = detail.data!;
      return productsApi.publish(
        productId!,
        data.product.version,
        data.draft_revision!.version,
      );
    },
  });

  const failedFiles = state.files.filter((file) => file.status === "failed");
  const variants =
    productId && detail.data?.draft_revision?.color_variants
      ? detail.data.draft_revision.color_variants
      : [];

  return (
    <section className="importRun" aria-label="Import progress">
      <PhaseLine state={state} />

      {state.message ? (
        <div
          className={
            state.phase === "failed" ? "notice notice--error" : "notice notice--warning"
          }
          role="alert"
        >
          {state.message}
        </div>
      ) : null}

      {state.warnings.length > 0 ? (
        <div className="notice notice--warning">
          <ul>
            {state.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Only the photographs that failed are listed. A wall of green ticks
          would bury the one line that needs the operator. */}
      {failedFiles.length > 0 ? (
        <ul className="retryList">
          {failedFiles.map((file) => (
            <li className="retryList__item" key={file.relativePath}>
              <span className="retryList__where">
                {file.colorName} · {ROLE_LABELS[file.role]}
              </span>
              <span className="retryList__file">{file.fileName}</span>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => onRetryFile(file.relativePath)}
              >
                Try this photograph again
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {state.phase === "complete" && productId ? (
        <div className="stack">
          <h3 className="review__title">What customers will see</h3>
          <p className="review__note">
            These are the actual pictures that were built. Check each colour before
            publishing.
          </p>

          {detail.isPending ? (
            <div className="empty" role="status">
              Loading the pictures…
            </div>
          ) : null}
          {detail.isError ? <ErrorNotice error={detail.error} /> : null}

          {variants.map((variant) => (
            <VariantReview key={variant.id} variant={variant} />
          ))}

          <div className="row row--wrap">
            <button
              type="button"
              className="btn"
              disabled={validate.isPending}
              onClick={() => validate.mutate()}
              data-testid="validate"
            >
              {validate.isPending ? "Checking…" : "Check the product"}
            </button>

            <button
              type="button"
              className="btn btn--primary"
              // Publishing is offered only once the backend has said the draft
              // is publishable. Pictures existing is not the same thing.
              disabled={
                !validate.data?.publishable || publish.isPending || publish.isSuccess
              }
              onClick={() => publish.mutate()}
              data-testid="publish"
            >
              {publish.isPending ? "Publishing…" : "Publish Product"}
            </button>
          </div>

          {validate.isError ? <ErrorNotice error={validate.error} /> : null}
          {validate.data ? <ValidationIssues validation={validate.data} /> : null}
          {publish.isError ? <ErrorNotice error={publish.error} /> : null}

          {publish.isSuccess ? (
            <div className="notice notice--success" role="status">
              <strong>{detail.data?.product.name} is live.</strong> Customers can see
              it in the shop now.
              <div className="row">
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => navigate("/")}
                  data-testid="back-to-dashboard"
                >
                  Back to Products
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {["review_needed", "failed", "unavailable", "cancelled"].includes(
        state.phase,
      ) ? (
        <p className="faint">
          Nothing has been published, and the photographs that did reach storage are
          still there.
        </p>
      ) : null}
    </section>
  );
}
